'use client'

import type { Pool } from '@sushiswap/graph-client/data-api'
import {
  Currency,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@sushiswap/ui'
import type { FC } from 'react'
import { useKatanaRewardCampaigns } from 'src/lib/hooks/react-query'
import { formatPercent } from 'sushi'
import { type EvmAddress, EvmChainId } from 'sushi/evm'

interface KatanaStakingAPRHoverCardProps {
  pool: Pool
}

export const KatanaStakingAPRHoverCard: FC<KatanaStakingAPRHoverCardProps> = ({
  pool,
}) => {
  const chainId = pool.chainId as EvmChainId

  const { data: katanaRewardCampaigns } = useKatanaRewardCampaigns({
    pool: pool.address as EvmAddress,
    chainId,
    enabled: chainId === EvmChainId.KATANA,
  })

  if (chainId !== EvmChainId.KATANA) return null

  const liveKatanaRewardCampaigns = katanaRewardCampaigns?.filter(
    (campaign) => campaign.isLive,
  )

  const katanaStakingApr = liveKatanaRewardCampaigns?.reduce(
    (total, campaign) => total + campaign.apr,
    0,
  )

  const showKatanaStakingApr =
    typeof katanaStakingApr === 'number' && katanaStakingApr > 0

  const card = (
    <div className="flex flex-col gap-3 p-6">
      <p className="font-medium">Stake to earn these emissions</p>
      <p className="text-sm text-muted-foreground">
        Stake to earn KAT rewards. Go to{' '}
        <a
          href="https://app.katana.network/portfolio"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium underline decoration-dotted underline-offset-2"
        >
          app.katana.network/portfolio
        </a>{' '}
        to stake and opt in.
      </p>
      <p className="text-sm text-muted-foreground">
        Your position moves to the external Katana staker contract while staked.
      </p>
    </div>
  )

  const trigger = (
    <div className="flex items-center gap-1">
      <span className="underline decoration-dotted underline-offset-2">
        {formatPercent(katanaStakingApr)}
      </span>
      {liveKatanaRewardCampaigns?.map((campaign) => (
        <Currency.Icon
          key={campaign.id}
          width={16}
          height={16}
          currency={campaign.rewardToken}
        />
      ))}
    </div>
  )

  if (showKatanaStakingApr) {
    return (
      <>
        <span className="text-muted-foreground">/</span>
        <div className="hidden sm:block">
          <HoverCard openDelay={300} closeDelay={0}>
            <HoverCardTrigger asChild className="cursor-pointer">
              {trigger}
            </HoverCardTrigger>
            <HoverCardContent side="right" className="!p-0 max-w-[320px]">
              {card}
            </HoverCardContent>
          </HoverCard>
        </div>
        <div className="block sm:hidden">
          <Popover>
            <PopoverTrigger
              onClick={(e) => e.stopPropagation()}
              asChild
              className="cursor-pointer"
            >
              {trigger}
            </PopoverTrigger>
            <PopoverContent side="right" className="!p-0 max-w-[320px]">
              {card}
            </PopoverContent>
          </Popover>
        </div>
      </>
    )
  }

  return <div className="min-w-[84px]" />
}
